import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'
import { GoogleSignInButton } from './GoogleSignInButton'
import { LinkedInSignInButton } from './LinkedInSignInButton'
import { EmailSignInForm } from './EmailSignInForm'
import { ResendVerificationForm } from '../ResendVerificationForm'
import { ForgotPasswordDialog } from '../ForgotPasswordDialog'
import { useTar } from '@/modules'
import { GoogleLoadingView, LinkedInLoadingView } from '../AuthComponents/AuthSocialLoadingViews'

export const LoginDialog = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const [isEmailLoading, setIsEmailLoading] = useState(false)
  const [showResendForm, setShowResendForm] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [lastEmailAttempt, setLastEmailAttempt] = useState('')
  const formRef = useRef<HTMLDivElement>(null)
  const { t } = useTar()

  const {
    isLoginDialogOpen,
    signInWithGoogle,
    signInWithLinkedIn,
    signInWithEmail,
    closeLoginDialog,
    openSignupDialog,
    error,
    errorCode,
    clearError
  } = useAuth()

  useEffect(() => {
    const resetSocialLoadingStates = () => {
      setIsGoogleLoading(false)
      setIsLinkedInLoading(false)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetSocialLoadingStates()
      }
    }

    window.addEventListener('pageshow', resetSocialLoadingStates)
    window.addEventListener('popstate', resetSocialLoadingStates)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('pageshow', resetSocialLoadingStates)
      window.removeEventListener('popstate', resetSocialLoadingStates)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isEmailLoading) return // Prevent submission if already loading

    const formData = new FormData(e.currentTarget)
    const email = formData.get('email') as string
    const password = formData.get('password') as string
    const rememberMe = formData.get('rememberMe') !== null

    // Store the email for potential resend use
    setLastEmailAttempt(email)

    if (signInWithEmail && email && password) {
      setIsEmailLoading(true)
      setShowResendForm(false) // Hide resend form on new attempt
      await signInWithEmail({ email, password, rememberMe })
      setIsEmailLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) return // Prevent multiple clicks
    try {
      setIsGoogleLoading(true)
      if (signInWithGoogle) {
        await signInWithGoogle()
      }
    } catch (error) {
      console.error('Google sign-in error:', error)
      setIsGoogleLoading(false)
      throw error
    }
  }

  const handleLinkedInSignIn = async () => {
    if (isLinkedInLoading) return // Prevent multiple clicks
    try {
      setIsLinkedInLoading(true)
      if (signInWithLinkedIn) {
        await signInWithLinkedIn()
      }
    } catch (error) {
      console.error('LinkedIn sign-in error:', error)
      setIsLinkedInLoading(false)
      throw error
    }
  }

  const handleResendSuccess = () => {
    setShowResendForm(false)
    // Clear the error to hide the error message and return to login view
    clearError()
  }

  const handleResendCancel = () => {
    setShowResendForm(false)
  }

  const isUnverifiedEmailError = errorCode === 'unverifiedEmail'

  const isAnyLoading = isGoogleLoading || isLinkedInLoading || isEmailLoading

  return (
    <Dialog
      open={isLoginDialogOpen}
      onOpenChange={(open) => !isAnyLoading && !open && closeLoginDialog()}
    >
      <DialogContent
        className="w-full h-full sm:w-auto sm:max-w-md rounded-none sm:rounded-lg sm:min-w-[480px]"
        onPointerDownOutside={(e) => isAnyLoading && e.preventDefault()}
        onEscapeKeyDown={(e) => isAnyLoading && e.preventDefault()}
        onInteractOutside={(e) => isAnyLoading && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {t('common.login')}
          </DialogTitle>
        </DialogHeader>

        {isLinkedInLoading ? (
          <LinkedInLoadingView />
        ) : isGoogleLoading ? (
          <GoogleLoadingView />
        ) : (
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-error">
                      {isUnverifiedEmailError ? t('auth.emailVerificationRequired') : t('auth.loginError')}
                    </h4>
                    <p className="text-sm text-error mt-1">
                      {error}
                    </p>
                  </div>
                </div>

                {/* Show resend verification form for unverified email */}
                {isUnverifiedEmailError && (
                  <div className="mt-3">
                    {!showResendForm ? (
                      <div className="space-y-2">
                        <p className="text-sm text-error">
                          {t('auth.emailVerificationDescription')}
                        </p>
                        <button
                          onClick={() => setShowResendForm(true)}
                          className="w-full bg-error hover:bg-error/90 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                        >
                          {t('auth.resendVerificationEmail')}
                        </button>
                      </div>
                    ) : (
                      <ResendVerificationForm
                        initialEmail={lastEmailAttempt}
                        onSuccess={handleResendSuccess}
                        onCancel={handleResendCancel}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-col space-y-3">
              {/* LinkedIn Sign-In Button */}
              <LinkedInSignInButton
                onClick={handleLinkedInSignIn}
                isLoading={isLinkedInLoading}
                isDisabled={isAnyLoading}
              />
              {/* Google Sign-In Button */}
              <GoogleSignInButton
                onClick={handleGoogleSignIn}
                isLoading={isGoogleLoading}
                isDisabled={isAnyLoading}
              />
            </div>
            {/* Email Sign-In Form */}
            <div ref={formRef}>
              <EmailSignInForm
                onSubmit={handleEmailSubmit}
                isLoading={isEmailLoading}
                isDisabled={isAnyLoading}
                onSignUp={() => openSignupDialog()}
                onForgotPassword={() => setShowForgotPassword(true)}
              />
            </div>
          </div>
        )}
      </DialogContent>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
        initialEmail={lastEmailAttempt}
      />
    </Dialog>
  )
} 